import { useMemo, useRef, useState } from 'react';
import { TripNeeds } from '../../components/TripExtras';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useIonAlert } from '@ionic/react';
import { Check, Clock, MessageCircle, X } from 'lucide-react';
import { Button, Card, ErrorState, Money, Notice, RequestStatusBadge, SectionTitle, Spinner } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { MapView } from '../../components/map/MapView';
import type { MarkerSpec } from '../../components/map/engine';
import { useToast } from '../../components/toast';
import { api, errorMessage, newIdempotencyKey } from '../../lib/api';
import { fmtDateTime, fmtDuration, fmtKm, relativeMinutes } from '../../lib/format';
import type { QuoteDto, RideRequestDto } from '../../lib/types';

export function routeMarkers(r: { origin: { lat: number; lng: number }; destination: { lat: number; lng: number }; stops?: Array<{ lat: number; lng: number }> }): MarkerSpec[] {
  return [
    { id: 'o', position: r.origin, kind: 'origin' },
    ...(r.stops ?? []).map((s, i) => ({ id: `s${i}`, position: s, kind: 'stop' as const, label: String(i + 1) })),
    { id: 'd', position: r.destination, kind: 'destination' },
  ];
}

function QuoteCard({ q, onAccept, onReject, busy }: { q: QuoteDto; onAccept: () => void; onReject: () => void; busy: boolean }) {
  return (
    <Card className="p-5 border-2 border-brand-red/60">
      <p className="text-sm text-[var(--text-muted)]">Precio propuesto por el conductor</p>
      <Money value={q.amount} className="block text-3xl font-extrabold tracking-tight" />
      {Number(q.tolls) > 0 && <p className="text-sm text-[var(--text-muted)]">Incluye peajes por <Money value={q.tolls} /></p>}
      {q.proposedScheduledAt && <Notice tone="warning">El conductor propone otro horario: <strong>{fmtDateTime(q.proposedScheduledAt)}</strong>{q.proposedReturnAt ? ` (regreso ${fmtDateTime(q.proposedReturnAt)})` : ''}.</Notice>}
      {q.includedServices.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {q.includedServices.map((s) => <li key={s} className="rounded-full bg-black/5 dark:bg-white/10 px-3 py-1 text-xs">{s}</li>)}
        </ul>
      )}
      {q.conditions && <p className="mt-3 text-sm">{q.conditions}</p>}
      <p className="mt-3 text-xs text-[var(--text-muted)] flex items-center gap-1"><Clock className="size-3.5" aria-hidden /> Válida por {relativeMinutes(q.expiresAt)}</p>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button variant="secondary" icon={<X className="size-5" />} disabled={busy} onClick={onReject}>Rechazar</Button>
        <Button variant="accent" icon={<Check className="size-5" />} loading={busy} onClick={onAccept}>Aceptar</Button>
      </div>
    </Card>
  );
}

export function RequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const qc = useQueryClient();
  const toast = useToast();
  const [alert] = useIonAlert();
  const [busy, setBusy] = useState(false);
  const acceptKey = useRef(newIdempotencyKey());
  const q = useQuery({ queryKey: ['ride-request', id], queryFn: () => api<RideRequestDto>(`/ride-requests/${id}`), refetchInterval: 30_000 });
  const r = q.data;
  const markers = useMemo(() => (r ? routeMarkers(r) : []), [r]);
  const pending = r?.quotes.find((x) => x.status === 'PENDING');

  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['ride-request', id] }), qc.invalidateQueries({ queryKey: ['ride-requests'] }), qc.invalidateQueries({ queryKey: ['rides'] })]);

  const accept = (quote: QuoteDto) =>
    alert({
      header: 'Confirmar reserva',
      message: `Aceptas el precio de RD$${Number(quote.amount).toLocaleString('es-DO', { minimumFractionDigits: 2 })}${quote.proposedScheduledAt ? ` para el ${fmtDateTime(quote.proposedScheduledAt)}` : ''}. El pago se realiza al conductor.`,
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        {
          text: 'Aceptar',
          handler: () => {
            void (async () => {
              setBusy(true);
              try {
                const res = await api<{ rideIds: string[]; seriesId: string | null }>(`/quotes/${quote.id}/accept`, { method: 'POST', idempotencyKey: acceptKey.current });
                await refresh();
                toast('¡Reserva confirmada!', 'success');
                if (res.rideIds[0]) history.replace(`/app/rides/${res.rideIds[0]}`);
                else if (res.seriesId) history.replace(`/app/recurring/${res.seriesId}`);
              } catch (e) {
                toast(errorMessage(e), 'danger');
                acceptKey.current = newIdempotencyKey();
                await refresh();
              } finally {
                setBusy(false);
              }
            })();
          },
        },
      ],
    });

  const reject = (quote: QuoteDto) =>
    alert({
      header: 'Rechazar cotización',
      inputs: [{ name: 'reason', type: 'text', placeholder: 'Motivo (opcional)' }],
      buttons: [
        { text: 'Volver', role: 'cancel' },
        { text: 'Rechazar', handler: (v: { reason: string }) => void api(`/quotes/${quote.id}/reject`, { body: { reason: v.reason || undefined } }).then(refresh).catch((e) => toast(errorMessage(e), 'danger')) },
      ],
    });

  const cancel = () =>
    alert({
      header: '¿Cancelar la solicitud?',
      buttons: [
        { text: 'No', role: 'cancel' },
        { text: 'Sí, cancelar', role: 'destructive', handler: () => void api(`/ride-requests/${id}/cancel`, { body: {} }).then(refresh).catch((e) => toast(errorMessage(e), 'danger')) },
      ],
    });

  return (
    <Page title="Solicitud" back="/app/trips" onRefresh={() => q.refetch()}>
      {q.isLoading && <Spinner />}
      {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
      {r && (
        <div className="flex flex-col gap-4">
          <MapView className="h-52 -mx-4 sm:mx-0 sm:rounded-3xl" markers={markers} route={r.route?.polyline ?? null} interactive={false} />
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-bold text-lg">{r.service.name}</h2>
              <p className="text-sm text-[var(--text-muted)]">{r.mode === 'NOW' ? 'Lo antes posible' : fmtDateTime(r.scheduledAt)}{r.roundTrip ? ` · regreso ${fmtDateTime(r.returnAt)}` : ''}</p>
            </div>
            <RequestStatusBadge status={r.status} />
          </div>

          {r.status === 'REQUESTED' && <Notice>Tu solicitud está pendiente de cotización. Recibirás una notificación con el precio.</Notice>}
          {r.status === 'INFO_REQUESTED' && r.infoRequest && <Notice tone="warning"><strong>El conductor pregunta:</strong> {r.infoRequest} Responde por el chat o ajusta los detalles.</Notice>}
          {r.status === 'REJECTED' && <Notice tone="danger">El conductor no está disponible{r.rejectionReason ? `: ${r.rejectionReason}` : '.'}</Notice>}
          {r.status === 'EXPIRED' && <Notice>La solicitud expiró sin confirmarse.</Notice>}

          {pending && <QuoteCard q={pending} busy={busy} onAccept={() => accept(pending)} onReject={() => reject(pending)} />}
          {['REQUESTED', 'INFO_REQUESTED', 'QUOTED'].includes(r.status) && (
            <Card className="p-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm text-[var(--text-muted)]">{r.offeredFare ? 'Tu oferta' : 'Aún no ofreces un precio'}</p>
                {r.offeredFare && <Money value={r.offeredFare} className="text-xl font-bold" />}
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  alert({
                    header: pending ? 'Ofrecer otro precio' : 'Ofrecer mi precio',
                    message: pending ? 'Tu conductor verá tu oferta y podrá aceptarla o enviarte otra cotización.' : 'Tu conductor podrá aceptarlo o enviarte su precio.',
                    inputs: [{ name: 'amount', type: 'number', placeholder: 'RD$', attributes: { inputmode: 'numeric', min: 1 } }],
                    buttons: [
                      { text: 'Cancelar', role: 'cancel' },
                      {
                        text: 'Enviar',
                        handler: (v: { amount: string }) => {
                          const n = Math.round(Number(v.amount));
                          if (!n || n <= 0) return false;
                          void api(`/ride-requests/${id}/offer`, { body: { amount: String(n) } })
                            .then(refresh)
                            .then(() => toast('Enviamos tu oferta', 'success'))
                            .catch((e) => toast(errorMessage(e), 'danger'));
                        },
                      },
                    ],
                  })
                }
              >
                {pending ? 'Contraofertar' : r.offeredFare ? 'Cambiar oferta' : 'Ofrecer precio'}
              </Button>
            </Card>
          )}

          {r.rides.length > 0 && (
            <Card className="p-4">
              <SectionTitle title="Tu reserva" />
              {r.rides.map((ride) => (
                <Link key={ride.id} to={`/app/rides/${ride.id}`} className="tap flex items-center justify-between py-2 text-sm font-medium text-brand-red">
                  {ride.leg === 'RETURN' ? 'Regreso' : ride.leg === 'OUTBOUND' ? 'Ida' : 'Viaje'} {ride.code} · {fmtDateTime(ride.scheduledAt)}
                </Link>
              ))}
            </Card>
          )}

          <Card className="p-4 text-sm flex flex-col gap-2">
            <p><span className="text-[var(--text-muted)]">Desde:</span> {r.origin.address}</p>
            {r.stops.map((s, i) => <p key={i}><span className="text-[var(--text-muted)]">Parada {i + 1}:</span> {s.address}</p>)}
            <p><span className="text-[var(--text-muted)]">Hasta:</span> {r.destination.address}</p>
            {r.route && <p><span className="text-[var(--text-muted)]">Recorrido:</span> {fmtKm(r.route.distanceMeters)} · {fmtDuration(r.route.durationSeconds)}{r.route.approximate ? ' (aprox.)' : ''}</p>}
            <p><span className="text-[var(--text-muted)]">Pasajeros:</span> {r.passengers} · <span className="text-[var(--text-muted)]">Maletas:</span> {r.luggage}</p>
            {r.notes && <p><span className="text-[var(--text-muted)]">Notas:</span> <span data-no-translate>{r.notes}</span></p>}
            <TripNeeds needs={r.needs} flightNumber={r.flightNumber} pkg={r.package} />
          </Card>

          <div className="flex flex-col gap-2">
            {r.conversationId && (
              <Link to={`/app/chat/${r.conversationId}`}><Button variant="secondary" block icon={<MessageCircle className="size-5" />}>Chat con el conductor</Button></Link>
            )}
            {['REQUESTED', 'INFO_REQUESTED', 'QUOTED'].includes(r.status) && <Button variant="danger" block onClick={cancel}>Cancelar solicitud</Button>}
          </div>
        </div>
      )}
    </Page>
  );
}
